import { cn } from '@/helpers/shadcn/utils'
import { cva } from 'class-variance-authority'
import { Icon } from '@/components/atoms'
import { useEffect } from 'react'

/**
 * Global floating toast for error messages.
 * Props: type ('success'|'error'|'info'|'warning'), message, show, onCloseClick, duration
 */
const ALERT_INLINE_STYLE = {
  position: 'fixed',
  top: 24,
  right: 24,
  zIndex: 9999,
  maxWidth: '400px',
  width: 'auto',
}

const TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  WARNING: 'warning',
}

const ICON_TYPES = {
  [TYPES.SUCCESS]: 'check_circle_outline',
  [TYPES.ERROR]: 'error',
  [TYPES.INFO]: 'info',
  [TYPES.WARNING]: 'warning_amber',
}

const AlertIcon = ({ type }) => (
  <div className="inline-flex h-6 w-6 items-center justify-center rounded-lg mt-0.5">
    <Icon icon={ICON_TYPES[type]} size="medium" className="text-button-icon" />
  </div>
)

const GlobalToasterAlert = ({ type, message, show = false, onCloseClick, duration = 5000 }) => {
  useEffect(() => {
    if (!show) return
    const itv = setTimeout(() => onCloseClick?.(), duration)
    return () => clearTimeout(itv)
  }, [message, onCloseClick, show, type, duration])

  const alertVariants = cva(
    `relative z-30 flex items-start w-full rounded-md px-4 py-2 alert max-w-[calc(100vw-7.5rem)]`,
    {
      variants: {
        type: {
          [TYPES.SUCCESS]: 'bg-support-success-primary text-white',
          [TYPES.ERROR]: 'bg-support-error-primary text-white',
          [TYPES.INFO]: 'bg-support-info-primary text-white',
          [TYPES.WARNING]: 'bg-[#CC6200] text-white',
        },
      },
    }
  )

  if (!show) return null

  return (
    <div
      data-testid={`alert-${type}`}
      className={cn(alertVariants({ type }))}
      role="alert"
      style={ALERT_INLINE_STYLE}
    >
      <AlertIcon type={type} />
      <div className="ml-2 flex-1 text-sm font-medium flex items-center min-h-[24px]">
        {message}
      </div>
      <button
        type="button"
        className="ml-4 text-white hover:text-gray-200 focus:outline-none"
        onClick={onCloseClick}
        aria-label="Close"
      >
        <Icon icon="close" size="small" />
      </button>
    </div>
  )
}

export default GlobalToasterAlert
